import { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { buildChargeBreakdown } from "../utils/chargeBreakdown";
import "../../../../css/Marketing/WebsiteCharges.css";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";

ChartJS.register(ArcElement, Tooltip, Legend);
const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    value,
  );

const MonthlyChargeBreakdown = ({ records, monthLabel, loading }) => {
  const { rows, total } = useMemo(
    () => buildChargeBreakdown(records),
    [records],
  );
  const [sort, setSort] = useState({ key: null, direction: 1 });
  const sorted = useMemo(
    () =>
      !sort.key
        ? rows
        : [...rows].sort(
            (a, b) =>
              (sort.key === "label"
                ? a.label.localeCompare(b.label)
                : a[sort.key] - b[sort.key]) * sort.direction,
          ),
    [rows, sort],
  );
  const sortBy = (key) =>
    setSort((previous) => ({
      key,
      direction: previous.key === key ? -previous.direction : 1,
    }));
  const chartRows = rows.filter((row) => row.amount > 0);
  return (
    <section
      className="monthly-charge-breakdown"
      aria-label="Monthly charge breakdown"
      aria-busy={loading}
    >
      <div>
        <div className="monthly-charge-breakdown-chart">
          {chartRows.length > 0 && !loading && (
            <Doughnut
              data={{
                labels: chartRows.map((row) => row.label),
                datasets: [
                  {
                    data: chartRows.map((row) => row.amount),
                    backgroundColor: chartRows.map((row) => row.color),
                    borderWidth: 0,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: "65%",
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (item) => `${item.label}: ${money(item.parsed)}`,
                    },
                  },
                },
              }}
              role="img"
              aria-label={`${monthLabel} charges by type. Total ${money(total)}. Details in the adjacent table.`}
            />
          )}
          <div className="monthly-charge-breakdown-centre">
            <span>{monthLabel}</span>
            <span>Total Charges</span>
            <strong>{loading ? "Loading…" : money(total)}</strong>
          </div>
        </div>
        <ul className="monthly-charge-breakdown-legend">
          {rows.map((row) => (
            <li key={row.id}>
              <span style={{ backgroundColor: row.color }} />
              {row.label}{" "}
              {loading ? "-" : `${Number(row.percentage.toFixed(1))}%`}
            </li>
          ))}
        </ul>
        {!loading && !chartRows.length && (
          <p className="FP_Help_Text">No charges for this month.</p>
        )}
        {rows.some((row) => row.amount < 0) && (
          <p className="FP_Help_Text">
            Credits are included in the table and total; the chart shows
            positive charges.
          </p>
        )}
      </div>
      <section className="website-dashboard-card table-scroll-card ">
        <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
          <table
            className="website-table website-charges-table monthly-charge-breakdown-table"
            aria-label="Charges by type"
          >
            <thead>
              <tr>
                {[
                  ["label", "Charge Type"],
                  [null, "Chargeable Records"],
                  ["amount", "Amount"],
                  ["percentage", "Total"],
                ].map(([key, label]) => (
                  <th
                    className="FP_Help_Text_Semibold"
                    key={label}
                    aria-sort={
                      key && sort.key === key
                        ? sort.direction === 1
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                  >
                    {key ? (
                      <button
                        type="button"
                        className="table-heading-content table-sort-button"
                        onClick={() => sortBy(key)}
                      >
                        {label} <SortIcon aria-hidden="true" />
                      </button>
                    ) : (
                      label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.id}>
                  <td className="FP_Help_Text black">{row.label}</td>
                  <td className="FP_Help_Text black">{row.count}</td>
                  <td className="FP_Help_Text black">
                    {loading ? "-" : money(row.amount)}
                  </td>
                  <td className="FP_Help_Text black">
                    {loading ? "-" : `${row.percentage.toFixed(1)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="website-charges-total-row">
                <td className="FP_Help_Text black">Total</td>
                <td className="FP_Help_Text black">
                  {rows.reduce((sum, row) => sum + row.count, 0)}
                </td>
                <td className="FP_Help_Text black">
                  {loading ? "-" : money(total)}
                </td>
                <td className="FP_Help_Text black">
                  {total > 0 ? "100%" : "-"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </section>
  );
};
export default MonthlyChargeBreakdown;
